import React from "react";
import { toODataString, process } from "@progress/kendo-data-query";
import Moment from "moment";
import "./WidgetGridLoader.scss";
import { Utils } from "../../helpers";

export class WidgetGridLoader extends React.Component {
  constructor(props) {
    super(props);
    this.core = props.core;
    this.helper = this.core?.make("oxzion/restClient");
    this.loader = this.core?.make("oxzion/splash");
    this.uuid = props.uuid;
    this.filterParams = props.filterParams;
    this.setIsFetching = props.setIsFetching;
    this.pending = "";
    this.lastSuccess = "";
    this.gridfilterString = "";
    this.lastResponseData = null;
    this.lastGroup = null;
    this.triggerGetCall = this.triggerGetCall.bind(this);
    this.debouncedTriggerGetCall = this.core?.make("oxzion/search")?.delayedSearch(this.triggerGetCall, 1000);
  }

  // function to create Grid Filter Parameters
  // params - The default filterGrid event parameters that are triggered by gridstateChange event
  createFilterString = (gridFilterParams) => {
    const formatDate = (val) => {
      return "date:" + Moment(val).format("YYYY-MM-DD HH:mm:ss");
    };

    const getOperator = (operator) => {
      const operators = {
        eq: "==",
        neq: "!=",
        gte: ">=",
        gt: ">",
        lte: "<=",
        lt: "<",
        startswith: "STARTSWITH",
        endswith: "ENDSWITH",
        contains: "LIKE",
        doesnotcontain: "NOT LIKE",
      };
      return operators[operator] || "==";
    };

    const isValidDate = (value) => {
      const date = new Date(value);
      return !isNaN(date.getTime()) && value === date.toISOString();
    };

    const processFilter = (filter) => {
      if (filter.filters) {
        // Recursively process nested groups
        const nested = processFilterGroup(filter);
        return nested;
      }

      let value = filter.value;

      if (typeof value === "string" && (Utils.isValidDateFormat(value) || isValidDate(value))) {
        value = formatDate(new Date(value));
      }

      const operator = getOperator(filter.operator);
      return [filter.field, operator, value];
    };

    const buildNestedLogicChain = (filters, logic) => {
      if (filters.length === 0) return null;
      if (filters.length === 1) return filters[0];

      let result = [filters[0], logic, filters[1]];
      for (let i = 2; i < filters.length; i++) {
        result = [result, logic, filters[i]];
      }
      return result;
    };

    const processFilterGroup = (group) => {
      const logic = (group.logic || "and").toUpperCase();
      const processed = group.filters.map(processFilter).filter((f) => f !== null);

      return buildNestedLogicChain(processed, logic);
    };

    const parsed = processFilterGroup(gridFilterParams);
    return JSON.stringify(parsed || []);
  };

  requestDataIfNeeded = () => {
    const { dataState } = this.props;
    const currentGroup = JSON.stringify(dataState?.group || []);

    // if (currentGroup === this.lastGroup) {
    //   if ((this.pending && this.pending !== undefined) || this.lastSuccess) {
    //     return;
    //   }
    // }
    this.lastGroup = currentGroup;

    let filtersApplied = "";
    if (typeof dataState.filter === "undefined") {
      // No filters applied, use the OData string directly
      filtersApplied = toODataString(dataState).replace(/\$/g, "");
    } else {
      // Create filter string for grid filter
      this.gridfilterString = this.createFilterString(dataState.filter);
      let tempDataState = JSON.parse(JSON.stringify(dataState));
      delete tempDataState.filter;

      filtersApplied = toODataString(tempDataState).replace(/\$/g, "");

      if (this.gridfilterString) {
        filtersApplied += `&filter_grid=${this.gridfilterString}`;
      }
    }

    this.pending = filtersApplied;

    // Debounced API call
    this.debouncedTriggerGetCall?.(this.uuid, this.filterParams, this.pending, filtersApplied);
  };

  triggerGetCall = async (uuid = this.uuid, filterParams = this.filterParams, pendingValue = this.pending, filtersApplied) => {
    if (filtersApplied === this.lastSuccess) {
      return; // Avoid duplicate API calls
    }
    try {
      const response = await this.getWidgetByUuid(uuid, filterParams, pendingValue);

      if (response?.status === "success") {
        this.lastSuccess = pendingValue;
        this.pending = "";
        if (filtersApplied === this.lastSuccess) {
          const finalData = response.data.widget.data;
          this.lastResponseData = finalData;

          // Handle grouping similar to DataLoader
          const groupConfig = this.props.dataState.group ? { group: this.props.dataState.group } : null;
          this.props.onDataRecieved({
            data: groupConfig ? process(finalData, groupConfig).data : finalData,
            total: response.data.widget.total_count,
          });
        } else {
          this.requestDataIfNeeded();
        }
      } else {
        // generate an alert for data issues
        this.props.notif?.current?.notify("Error", "Invalid search or filter input. Please try again with different input.", "danger");
        this.pending = "";
      }
    } catch (error) {
      console.error("Error", error);
      this.pending = "";
    } finally {
      this.setIsFetching?.(false);
    }
  };

  async getWidgetByUuid(uuid, filterParams, gridParams) {
    let filterParameter = filterParams && filterParams != [] && filterParams.length != 0 ? "&filter=" + JSON.stringify(filterParams) : "";
    // send this filters to widgets as well so that we can append those to the url that we are trying to create
    let response = await this.helper?.request("v1", "analytics/widget/" + uuid + "?data=true" + filterParameter + "&" + gridParams, {}, "get");
    return response;
  }

  componentDidUpdate(prevProps) {
    // Check if group configuration has changed and reprocess data
    const currentGroup = JSON.stringify(this.props.dataState?.group || []);
    const prevGroup = JSON.stringify(prevProps.dataState?.group || []);

    if (currentGroup !== prevGroup && this.lastResponseData) {
      this.lastGroup = currentGroup;
      this.reprocessDataWithGroup();
      return;
    }

    // Check for other dataState changes
    if (JSON.stringify(prevProps.dataState) !== JSON.stringify(this.props.dataState)) {
      this.setIsFetching?.(true);
      this.requestDataIfNeeded();
    }
  }

  componentDidMount() {
    setTimeout(() => {
      const oxGridElement = this.props.gridRef?.current?.querySelector(".oxGrid");
      oxGridElement && this.loader?.show(oxGridElement);
    }, 0);

    setTimeout(() => {
      this.loader?.destroy();
    }, 1000);

    // Initialize lastGroup
    this.lastGroup = JSON.stringify(this.props.dataState?.group || []);
  }

  // function to reprocess data with group configuration
  reprocessDataWithGroup = () => {
    if (this.lastResponseData) {
      const groupConfig = this.props.dataState.group ? { group: this.props.dataState.group } : null;
      this.props.onDataRecieved({
        data: groupConfig ? process(this.lastResponseData, groupConfig).data : this.lastResponseData,
        total: this.lastResponseData?.total ?? this.lastResponseData?.length ?? 0,
      });
    }
  };

  render() {
    return <div></div>;
  }
}
